import { useState, useEffect, useRef } from "react";
import { Save, FileText, Plus, X as XIcon } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const FIELDS = [
  { key: "section_label", label: "Etiqueta (pill del formulario)", placeholder: "Formulario de Inscripción" },
  { key: "title", label: "Título principal", placeholder: "¡Reserva tu plaza!" },
  { key: "semana1_label", label: "Texto checkbox Semana 1", placeholder: "Semana 1: del 22 al 26 de Junio 2026" },
  { key: "semana2_label", label: "Texto checkbox Semana 2", placeholder: "Semana 2: del 29 de Junio al 3 de Julio 2026" },
  { key: "madrugadores_label", label: "Label servicio madrugadores", placeholder: "Servicio madrugadores (8:00h)" },
  { key: "recogida_label", label: "Label recogida ampliada", placeholder: "Recogida ampliada (15:00h)" },
  { key: "btt_label1", label: "BTT — Condición 1 (bicicleta)", placeholder: "Traerá bicicleta en buen estado y con casco" },
  { key: "btt_label2", label: "BTT — Condición 2 (cámaras gel)", placeholder: "Traerá cámaras con gel antipinchazos" },
  { key: "btt_label3", label: "BTT — Condición 3 (cámara repuesto)", placeholder: "Traerá una cámara de repuesto" },
  { key: "btt_note", label: "Nota informativa BTT", placeholder: "* Sin bicicleta en condiciones no se puede participar en BTT." },
  { key: "btn_next", label: 'Texto botón "Siguiente"', placeholder: "Siguiente" },
  { key: "btn_submit", label: 'Texto botón "Enviar"', placeholder: "Enviar inscripción" },
];

const TEXT_AREAS = [
  { key: "auth_text", label: "Texto autorización de participación" },
  { key: "gdpr_text", label: "Texto protección de datos (RGPD)" },
];

const DEFAULTS = {
  section_label: "Formulario de Inscripción",
  title: "¡Reserva tu plaza!",
  semana1_label: "Semana 1: del 22 al 26 de Junio 2026",
  semana2_label: "Semana 2: del 29 de Junio al 3 de Julio 2026",
  madrugadores_label: "Servicio madrugadores (8:00h)",
  recogida_label: "Recogida ampliada (15:00h)",
  btt_label1: "Traerá bicicleta en buen estado y con casco",
  btt_label2: "Traerá cámaras con gel antipinchazos",
  btt_label3: "Traerá una cámara de repuesto",
  btt_note: "* Sin bicicleta en condiciones no se puede participar en BTT.",
  auth_text: "Autorizo la participación del/la menor en todas las actividades del Campus Verano Activo La Estanca 2026 (kayak, paddle surf, orientación, BTT, aula de naturaleza, realidad aumentada), así como el uso de fotografías/vídeos para fines informativos o difusión en redes sociales.",
  gdpr_text: "Los datos personales se utilizarán exclusivamente para la gestión del campus y no serán cedidos a terceros, conforme a la normativa vigente de protección de datos.",
  btn_next: "Siguiente",
  btn_submit: "Enviar inscripción",
};

export default function FormManager() {
  const { content, updateSection } = useContent();
  const [data, setData] = useState({ ...DEFAULTS, tallas_camiseta: ["XS", "S", "M", "L", "XL", "XXL"] });
  const [tallaInput, setTallaInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (content.form_config && !initialized.current) {
      initialized.current = true;
      setData({ ...DEFAULTS, tallas_camiseta: ["XS", "S", "M", "L", "XL", "XXL"], ...content.form_config });
    }
  }, [content.form_config]);

  const handleChange = (e) => setData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSection("form_config", data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="form-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Formulario de Inscripción</h1>
          <p className="text-[#475569] font-['Montserrat'] text-sm">Edita los textos, labels y botones del formulario visible en la landing page</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-form-config-btn"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-['Montserrat'] font-semibold text-sm transition-all ${saved ? "bg-green-500 text-white" : "bg-[#0072A4] text-white hover:bg-[#005A80]"} disabled:opacity-60`}>
          <Save size={15} />
          {saving ? "Guardando..." : saved ? "¡Guardado!" : "Guardar cambios"}
        </button>
      </div>

      {/* Inputs */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
        <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[#E2E8F0]">
          <FileText size={15} color="#0072A4" />
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] text-base">Textos e identificadores</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {FIELDS.map(f => (
            <div key={f.key} className={f.key === "btt_note" || f.key === "title" || f.key === "section_label" ? "sm:col-span-2" : ""}>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">{f.label}</label>
              <input name={f.key} value={data[f.key] || ""} onChange={handleChange}
                data-testid={`field-${f.key}`}
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]"
                placeholder={f.placeholder} />
            </div>
          ))}
        </div>
      </div>

      {/* Tallas camiseta */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
        <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] text-base mb-1 pb-4 border-b border-[#E2E8F0]">
          Tallas disponibles en el formulario
        </h3>
        <p className="text-[#94A3B8] text-xs font-['Montserrat'] mb-4 pt-3">
          Configura qué tallas de camiseta aparecen en el selector del formulario de inscripción.
        </p>
        {/* Current tallas chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="tallas-list">
          {(data.tallas_camiseta || []).map((t, i) => (
            <span key={t} className="inline-flex items-center gap-1.5 bg-[#0072A4]/10 text-[#0072A4] font-['Montserrat'] font-bold text-sm px-3 py-1.5 rounded-full">
              {t}
              <button type="button" onClick={() => setData(p => ({ ...p, tallas_camiseta: p.tallas_camiseta.filter((_, idx) => idx !== i) }))}
                data-testid={`remove-talla-${t}`}
                className="hover:text-red-500 transition-colors ml-0.5">
                <XIcon size={12} />
              </button>
            </span>
          ))}
        </div>
        {/* Add talla */}
        <div className="flex gap-2">
          <input
            value={tallaInput} onChange={e => setTallaInput(e.target.value.toUpperCase())}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                const t = tallaInput.trim();
                if (t && !(data.tallas_camiseta || []).includes(t)) {
                  setData(p => ({ ...p, tallas_camiseta: [...(p.tallas_camiseta || []), t] }));
                }
                setTallaInput("");
              }
            }}
            data-testid="talla-input"
            className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4] w-32"
            placeholder="Ej: XXL" maxLength={8} />
          <button type="button" data-testid="add-talla-btn"
            onClick={() => {
              const t = tallaInput.trim();
              if (t && !(data.tallas_camiseta || []).includes(t)) {
                setData(p => ({ ...p, tallas_camiseta: [...(p.tallas_camiseta || []), t] }));
              }
              setTallaInput("");
            }}
            className="flex items-center gap-1.5 bg-[#0072A4] text-white font-['Montserrat'] font-semibold px-4 py-2 rounded-lg text-sm hover:bg-[#005A80] transition-colors">
            <Plus size={14} /> Añadir
          </button>
        </div>
      </div>

      {/* Textareas */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
        <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] text-base mb-4 pb-4 border-b border-[#E2E8F0]">Textos legales y de autorización</h3>
        <div className="space-y-5">
          {TEXT_AREAS.map(f => (
            <div key={f.key}>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">{f.label}</label>
              <textarea name={f.key} value={data[f.key] || ""} onChange={handleChange} rows={4}
                data-testid={`field-${f.key}`}
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4] resize-none" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
